import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  AppContent,
  AppPage,
  ErrorComponents,
  SummaryGridItem,
  SummarySection,
  Breadcrumbs,
  PageHeaderV2,
  useFeatureToggle,
  useTitle,
} from '@css-xp/base';
import { AnalyticsEventSource, ScreenEventAnalytics } from '@atlassian/temp-analytics';
import Button from '@atlaskit/button';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { FetchTransactionAccountQuery, TransactionAccount, generatedTypesNew } from '@css-xp/graphtypes';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { ENABLE_RECENTLY_VISITED_HISTORY, removeContentScrollingHack } from '@atlassian/cssxp-utils-generic';
import { fetchTransactionAccount } from './queries/fetch-transaction-account-page';
import TransactionAccountBalanceTable from './component/transaction-account-balance-table';
import GetAccountBalance from './component/get-account-balance';
import ArrowLeftCircleIcon from '@atlaskit/icon/glyph/arrow-left-circle';
import './transaction-account-balance-page.scss';

const getTransactionAccount = (queryData: FetchTransactionAccountQuery) => {
  return queryData?.transactionAccount as TransactionAccount;
};

interface TransactionAccountBalancePageProps {
  appPageClass?: string;
  title?: string;
}

export const TransactionAccountBalancePage = ({ appPageClass, title }: TransactionAccountBalancePageProps) => {
  useEffect(() => {
    removeContentScrollingHack();
  }, []);

  const { id } = useParams<{ id: string }>();
  const [isEnabled] = useFeatureToggle();

  const transactionAccountPageResult = useQuery<
    generatedTypesNew.FetchTransactionAccountQuery,
    generatedTypesNew.FetchTransactionAccountQueryVariables
  >(fetchTransactionAccount, {
    variables: { id: id },
    fetchPolicy: 'no-cache',
    skip: !id,
  });
  const { data, loading, error } = transactionAccountPageResult;
  if (isEnabled(ENABLE_RECENTLY_VISITED_HISTORY)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useTitle(data?.transactionAccount.displayName ?? '', title);
  }

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;
  const transactionAccount = data?.transactionAccount;
  const transactionAccountId = transactionAccount?.id ?? id;
  const billToAddress = transactionAccount?.billToParty;

  const renderBreadcrumbs = () => {
    if (transactionAccount?.transactionAccountNumber) {
      const breadcrumbTrail = [
        {
          title: billToAddress?.name ?? 'Billing account',
          href: window.location.pathname,
        },
        {
          title: transactionAccount?.transactionAccountNumber,
          href: `/transaction-account/v2/${id}`,
        },
        {
          title: 'Account balance',
          href: window.location.pathname,
        },
      ];

      return <Breadcrumbs items={breadcrumbTrail} />;
    }
  };

  return (
    <TransactionAccountContext.Provider
      value={{
        getTransactionAccount,
        ...transactionAccountPageResult,
      }}
    >
      <AppPage className={appPageClass + ' txa-account-balance-page'}>
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.TRANSACTION_ACCOUNT_BALANCE,
            attributes: {
              transactionAccountNumber: transactionAccount?.transactionAccountNumber,
              transactionAccountId,
              id,
              entityName: title,
            },
          }}
        />
        {renderBreadcrumbs()}
        <AppContent className="txa-balance-page-container">
          <PageHeaderV2
            header={<GetAccountBalance transactionAccountId={transactionAccountId} />}
            actions={
              <div className="txa-balance-actions">
                <Button
                  iconBefore={<ArrowLeftCircleIcon label="left-arrow" />}
                  appearance="subtle"
                  onClick={() => history.back()}
                  className="balance-back-button"
                >
                  Back
                </Button>
              </div>
            }
          />
          <div className="txa-balance-table-container">
            <TransactionAccountBalanceTable
              label="Account credits and debits"
              transactionAccountId={transactionAccountId}
            />
          </div>
        </AppContent>
      </AppPage>
    </TransactionAccountContext.Provider>
  );
};

export default TransactionAccountBalancePage;
